/**
 * Packet upload + voice/chat strip for thin intake (unified into agentic shell).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SendIcon from '@mui/icons-material/Send';
import {
  getDiscoverySessionApi,
  importDiscoveryPacketApi,
  intakeChatApi,
} from '../../api/applicationType.api.js';
import { useSpeechInput } from '../../hooks/useSpeechInput';
import ClawDecisionPanel from '../demo/ClawDecisionPanel';

const ACCENT = '#1565c0';

export default function CitizenIntakeChatStrip({
  sessionId,
  packageId = 'san-francisco',
  onSession = null,
  compact = true,
}) {
  const [session, setSession] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    try {
      const s = await getDiscoverySessionApi(sessionId);
      setSession(s);
      if (onSession) onSession(s);
    } catch (_) {
      /* session may not exist yet */
    }
  }, [sessionId, onSession]);

  useEffect(() => { refresh(); }, [refresh]);

  const send = useCallback(async (raw) => {
    const text = String(raw || '').trim();
    if (!text || !sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const data = await intakeChatApi(sessionId, text, { agentLoop: true });
      const s = data?.session || null;
      setSession(s);
      if (onSession) onSession(s);
      setInput('');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Intake chat failed');
    } finally {
      setBusy(false);
    }
  }, [sessionId, onSession]);

  const onSpeech = useCallback((text) => {
    setInput((prev) => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}${text}`);
  }, []);
  const {
    isListening, startListening, stopListening, hasSupport,
  } = useSpeechInput(onSpeech);

  const onUploadPacket = async (file) => {
    if (!file || !sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const text = await file.text();
      const packet = JSON.parse(text);
      const s = await importDiscoveryPacketApi(sessionId, packet, { packageId });
      setSession(s);
      if (onSession) onSession(s);
      setToast('Packet imported');
    } catch (e) {
      setError(e?.message || 'Could not import packet JSON');
    } finally {
      setBusy(false);
    }
  };

  if (!sessionId) {
    return (
      <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: '#fff' }}>
        <Typography variant="caption" color="text.secondary">
          Continue from the advisor to attach a discovery session, or submit the form with a session id.
        </Typography>
      </Box>
    );
  }

  const chat = (session?.chatHistory || []).slice(-6);

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: '#fff',
        px: { xs: 1.5, sm: 2 },
        py: 1.25,
      }}
      data-testid="citizen-intake-chat-strip"
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ fontWeight: 700, color: ACCENT }}>
            Chat / voice / packet
          </Typography>
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={<CloudUploadOutlinedIcon />}
            disabled={busy}
            sx={{ textTransform: 'none', borderColor: ACCENT, color: ACCENT }}
          >
            Upload packet JSON
            <input
              type="file"
              hidden
              accept="application/json,.json"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUploadPacket(f);
                e.target.value = '';
              }}
            />
          </Button>
          {toast && (
            <Typography variant="caption" color="text.secondary">{toast}</Typography>
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}

        {!compact && chat.length > 0 && (
          <Stack spacing={0.5} sx={{ maxHeight: 120, overflowY: 'auto' }}>
            {chat.map((m, i) => (
              <Typography
                key={`${m.at || i}-${m.role}`}
                variant="caption"
                sx={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  bgcolor: m.role === 'user' ? 'rgba(21,101,192,0.1)' : 'rgba(15,23,42,0.04)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  maxWidth: '90%',
                }}
              >
                {m.content}
              </Typography>
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={0.75} alignItems="flex-end">
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={3}
            placeholder={session?.gaps?.nextAsk
              ? `Answer: ${session.gaps.nextAsk.label}`
              : 'Tell us what to fill next…'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            disabled={busy}
          />
          {hasSupport && (
            <IconButton
              color={isListening ? 'error' : 'primary'}
              onClick={() => (isListening ? stopListening() : startListening())}
              disabled={busy}
              aria-label={isListening ? 'Stop listening' : 'Speak'}
            >
              {isListening ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
          )}
          <IconButton
            color="primary"
            onClick={() => send(input)}
            disabled={busy || !input.trim()}
            aria-label="Send"
          >
            {busy ? <CircularProgress size={20} /> : <SendIcon />}
          </IconButton>
        </Stack>

        {session?.claw?.skillId && (
          <ClawDecisionPanel
            claw={session.claw}
            accent={ACCENT}
            defaultOpen={false}
            audience="citizen"
          />
        )}
      </Stack>
    </Box>
  );
}
